import { reconstitutedException } from '@mimir/common/util/exception';
import { invariant } from '@mimir/common/util/invariant';
import type { DvrNleOps } from '@mimir/primitive/nle/dvr/dvrNleOps';
import { NleEventType, type EncoderJobEvent, type NleEventBody } from '@mimir/primitive/nle/event';
import type { LogEntry } from '@mimir/primitive/structuredLogger';

// ── DvrNleOps ─────────────────────────────────────────────────────────────────
export function createDvrNleOps(): DvrNleOps {
  const eventHandlers = new Map<NleEventType, (msg: NleEventBody) => void>();

  function dispatch(type: NleEventType, msg: NleEventBody): void {
    eventHandlers.get(type)?.(msg);
  }

  return new Proxy({} as DvrNleOps, {
    get(_, method: string) {
      if (method === 'nleHostId') return 'resolve';
      if (method === 'onEvent') {
        return <T extends NleEventType>(type: T, handler: (msg: NleEventBody<T>) => void): () => void => {
          invariant(window.mimirElectron, 'window.mimirElectron is not available. This function must be called in a renderer process.');
          // DVR does not send CEP-style events — it notifies via IPC instead.
          // ProjectChanged fires when DVR clip data changes (e.g. after updateProjectItem).
          // EncoderJob fires during DVR render job.
          // DVR mode can be changed in Desktop app because of that we need to register
          // the event handlers every time the plugin is connected to DVR.
          let subscription: (() => void) | undefined;
          switch (type) {
            case NleEventType.ProjectChanged:
              subscription = window.mimirElectron.onRefreshClipInfos(() => dispatch(type, {}));
              break;
            case NleEventType.EncoderJob:
              subscription = window.mimirElectron.onRenderEvent((event: EncoderJobEvent) => dispatch(type, event));
              break;
            case NleEventType.LogMessage:
              subscription = window.mimirElectron.onLogEvent((event: LogEntry) => dispatch(type, event));
              break;
          }
          eventHandlers.set(type, handler as (msg: NleEventBody) => void);
          return () => {
            eventHandlers.delete(type);
            subscription?.();
          };
        };
      }
      return (...args: unknown[]) => invokeIpc(method, args);
    },
  });
}

// ── invokeIpc ─────────────────────────────────────────────────────────────────

async function invokeIpc(method: string, args: unknown[]): Promise<unknown> {
  invariant(window.mimirElectron, 'window.mimirElectron is not available. This function must be called in a renderer process.');
  const reply = await window.mimirElectron.mainRequire('./dvr/actions').callDvrAction(method, args);

  if (reply.error !== undefined) {
    throw reconstitutedException(reply.error);
  }
  return reply.result;
}